import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowCounterClockwise,
  CaretLeft,
  CaretRight,
  CheckCircle,
  LockSimple,
  Pulse,
  WarningCircle,
} from "@phosphor-icons/react";
import seed from "./data/seed.json";
import {
  CAPEX_EXPECTATIONS,
  capexExpectationForYear,
  largestBuysideGaps,
} from "./dataSources/capexExpectations";
import {
  ChartWall,
  InvestorGlossary,
  MetricChart,
  type DashboardModel,
} from "./components/ChartBoard";
import { CompanyCapexPanel } from "./components/model-panels/CompanyCapexPanel";
import { ComputeDeploymentPanel } from "./components/model-panels/ComputeDeploymentPanel";
import { FundingPressurePanel } from "./components/model-panels/FundingPressurePanel";
import { PowerDemandPanel } from "./components/model-panels/PowerDemandPanel";
import { TokenDemandPanel } from "./components/model-panels/TokenDemandPanel";
import { TokenVolumePanel } from "./components/model-panels/TokenVolumePanel";
import { LabArrPanel } from "./components/model-panels/LabArrPanel";
import { RealtimeMonitorBoard } from "./components/RealtimeMonitorBoard";
import {
  DEFAULT_RESEARCH_SCENARIO,
  runResearchModels,
  type ResearchModelBundle,
  type ResearchScenario,
} from "./research";
import type { DataPanel } from "./types";

type ModelId = "capex" | "token" | "tokenVolume" | "labArr" | "compute" | "funding" | "power";
type RuntimePanel = DataPanel & { runtime?: { storage?: string } };

interface AppProps {
  initialModelId?: ModelId;
}

interface ScenarioControlSpec {
  key: keyof ResearchScenario;
  label: string;
  help: string;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
}

const MODEL_DESCRIPTIONS: Record<ModelId, string> = {
  capex: "大厂还会投入多少钱？",
  token: "AI 使用量是否加速？",
  tokenVolume: "全球 Token 工作量有多大？",
  labArr: "OpenAI 与 Anthropic 如何变现？",
  compute: "投入转成了多少算力？",
  funding: "现金流还能覆盖投入吗？",
  power: "算力将需要多少电力？",
};

/**
 * Always-on plain-language blocks per model: what we compute and how to read
 * the chart. Keep these free of hard-coded data values; numbers belong to the
 * dynamic narrative fields.
 */
const MODEL_PLAIN: Record<ModelId, { method: string; reading: string }> = {
  capex: {
    method:
      "把 Alphabet、Microsoft、Amazon、Meta、Oracle 财报中的现金资本开支按同一季度加总。预测时对每家公司分别尝试三种简单规则——沿用上季、顺着近一年趋势、用历史滞后值做轻量回归——谁过去猜得准就用谁，再把五家加总。",
    reading:
      "实线是财报已披露的真实支出，虚线是模型外推，阴影是按历史真实预测误差画出的可能范围。这是五家公司的全部资本开支（不只 AI 部分），也不是管理层指引。",
  },
  token: {
    method:
      "用『开发者下载 AI 开发包的次数 × 维基百科 AI 词条阅读量』合成月度热度指数（2023 年 1 月 = 100），每月自动挑最近一年里猜得最准的简单外推规则，向前推 12 个月。",
    reading:
      "指数只回答“AI 采用热度在加速还是降温”。它不是全球 Token 总量，指数翻倍也不等于相关公司收入翻倍；OpenRouter 平台实测量用来交叉检验方向。",
  },
  tokenVolume: {
    method:
      "把办公、Coding、文件处理、研究和客服拆成 Chat 与 Agent 两种形态，再用可用人群、渗透率、活跃率、任务次数、每步 Token 和 Agent 步数逐层相乘。另用 OpenRouter 的真实平台 Token 做趋势回测，但不把单平台直接放大成全球份额。",
    reading:
      "当前点和未来带是低/中/高结构估计，不是电表式实测；OpenRouter 平台趋势有真实样本外回测。要同时看 Agent 占比、任务长度与算力约束，不能把结构估计的区间读成 90% 置信区间。",
  },
  labArr: {
    method:
      "先用当时可见的历史锚点，分别比较对数趋势、净新增 ARR 速度、采用驱动和沿用上次披露；再把冠军模型的公司总年化收入速度按 Coding、企业/API、通用订阅假设拆分。情景参数只改变未来分部。",
    reading:
      "公开锚点、最新完整月估计、截至今日估计和外部模型参照是四种不同证据。ARR 是短期年化 run-rate，不是 GAAP 收入；外部参照不进入拟合，远期路径超出真实回测跨度后只能作为情景外推。",
  },
  compute: {
    method:
      "把 Epoch 统计的每季度 AI 芯片交付（英伟达、AMD、谷歌、华为等）按芯片性能折算成统一的“交付算力指数”，同时算出对应的芯片支出金额；未来四个季度按近几季平均增量外推。",
    reading:
      "这条线回答“砸下去的钱变成了多少可用算力”。当前季度只有部分厂商可测，所以它是研究级方向指标，短期跳动可能来自统计覆盖变化而非真实拐点。",
  },
  funding: {
    method:
      "把五家公司“赚进来的经营现金”与“花出去的资本开支”放上同一天平：比值达到 1.00× 就是当季赚的现金全部花光。基准路径由样本外表现最好的近窗趋势与季节性模型生成；完整八季度残差路径不足时，越线概率保持未校准。",
    reading:
      "黑线是已披露实际，蓝色虚线是样本外选中模型的动态路径，阴影是历史预测落差的范围。红色 1.00× 表示当季 CapEx 用完经营现金；越线是资金压力预警，不是破产概率。样本不足时不显示伪精确的越线概率。",
  },
  power: {
    method:
      "像记流水账：每季交付的 AI 芯片按“延迟一个季度上架、每年约 6% 退役”累积成存量功率，再乘上机房配套、利用率与制冷开销，换算成电网需要供应的功率（GW）与用电量（TWh）。",
    reading:
      "这是由芯片存量推导的物理估算，不是电表实测。它回答“如果算力如期上架，电网要准备多少供电能力”；项目延期会把需求向后推移。",
  },
};

const SCENARIO_CONTROLS: Record<ModelId, ScenarioControlSpec[]> = {
  capex: [{
    key: "capexIntensity",
    label: "CapEx 强度",
    help: "直接乘到未来公司级 CapEx；历史 SEC 实际和回测不变。",
    min: 0.5,
    max: 1.8,
    step: 0.05,
    format: (value) => `${value.toFixed(2)}×`,
  }],
  token: [
    {
      key: "tokenAnnualDemandGrowthPct",
      label: "AI 需求年增速",
      help: "按 horizon 复利作用于未来月度需求指数。",
      min: -30,
      max: 120,
      step: 5,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      key: "tokenUnitPriceChangePct",
      label: "单位推理价格变化",
      help: "价格下降通过显式价格弹性提高未来需求，不回写历史。",
      min: -70,
      max: 40,
      step: 5,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      key: "tokenPriceElasticity",
      label: "价格弹性",
      help: "需求对单位价格变化的响应系数。",
      min: 0,
      max: 2,
      step: 0.1,
      format: (value) => value.toFixed(1),
    },
  ],
  tokenVolume: [
    {
      key: "tokenAiPenetrationMultiplier",
      label: "AI 使用渗透率",
      help: "直接改变未来结构估计中的活跃使用人群，不改历史平台回测。",
      min: 0.5,
      max: 2,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
    {
      key: "tokenAgentSharePct",
      label: "Agent Token 占比",
      help: "重新分配 Chat 与 Agent 的未来工作量；Chat 占比等于 100% 减本值。",
      min: 20,
      max: 98,
      step: 2,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      key: "tokenTaskLengthMultiplier",
      label: "平均任务长度",
      help: "同时缩放每项任务的 Tokens/step；Agent 步数仍单独保留。",
      min: 0.5,
      max: 2,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
    {
      key: "tokenAnnualDemandGrowthPct",
      label: "额外年需求增速",
      help: "叠加在平台趋势上的未来压力测试，不进入历史拟合。",
      min: -30,
      max: 100,
      step: 5,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      key: "tokenComputeCapacityMultiplier",
      label: "可服务算力",
      help: "低于 1 时限制可服务 Token；高于 1 不机械制造需求。",
      min: 0.5,
      max: 1.5,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
  ],
  labArr: [
    {
      key: "arrCodingPaidUserMultiplier",
      label: "Coding 付费用户",
      help: "只改变 Coding plan 分部的未来 ARR。",
      min: 0.5,
      max: 2,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
    {
      key: "arrB2bApiAnnualGrowthPct",
      label: "B 端 API 年增速",
      help: "作用于企业/API 分部的未来可计费需求。",
      min: -30,
      max: 150,
      step: 5,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      key: "arrEnterpriseSeatMultiplier",
      label: "企业座席渗透",
      help: "改变 B 端 API/企业分部的付费覆盖，不改变历史锚点。",
      min: 0.5,
      max: 2,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
    {
      key: "arrEffectivePriceMultiplier",
      label: "综合净价",
      help: "折扣、缓存和产品 mix 后的净价压力测试。",
      min: 0.5,
      max: 1.5,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
    {
      key: "arrComputeCapacityMultiplier",
      label: "可服务算力",
      help: "只在低于 1 时限制变现；算力不是加法收入。",
      min: 0.5,
      max: 1.5,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
  ],
  compute: [],
  funding: [
    {
      key: "fundingAnnualCapexGrowthDeltaPct",
      label: "CapEx 年增速增量",
      help: "直接改变未来现金 CapEx 路径。",
      min: -40,
      max: 60,
      step: 5,
      format: (value) => `${value.toFixed(0)}pp`,
    },
    {
      key: "fundingAnnualOcfGrowthDeltaPct",
      label: "OCF 年增速增量",
      help: "直接改变未来经营现金流和资金覆盖率。",
      min: -40,
      max: 60,
      step: 5,
      format: (value) => `${value.toFixed(0)}pp`,
    },
  ],
  power: [
    {
      key: "powerDeploymentLagQuarters",
      label: "部署滞后",
      help: "芯片交付到形成可用 IT 铭牌功率的季度数。",
      min: 0,
      max: 4,
      step: 1,
      format: (value) => `${value.toFixed(0)}Q`,
    },
    {
      key: "powerUtilization",
      label: "IT 利用率",
      help: "直接把 IT 铭牌功率转换为估算 IT Load。",
      min: 0.35,
      max: 1,
      step: 0.05,
      format: (value) => `${(value * 100).toFixed(0)}%`,
    },
    {
      key: "powerPue",
      label: "PUE",
      help: "直接把 IT Load 转换为 Facility Load 与 TWh。",
      min: 1.05,
      max: 1.7,
      step: 0.05,
      format: (value) => `${value.toFixed(2)}×`,
    },
  ],
};

function money(value: number, digits = 1) {
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}B`;
}

function number(value: number, digits = 1) {
  return value.toLocaleString("zh-CN", { maximumFractionDigits: digits });
}

function percent(value: number, digits = 1) {
  return `${(value * 100).toFixed(digits)}%`;
}

function signed(value: number, formatter: (input: number) => string) {
  if (Math.abs(value) < 1e-9) return "与基准一致";
  return `较基准 ${value > 0 ? "+" : "−"}${formatter(Math.abs(value))}`;
}

function signedMoney(value: number) {
  if (Math.abs(value) < 1e-9) return "$0.0B";
  return `${value > 0 ? "+" : "−"}$${Math.abs(value).toFixed(1)}B`;
}

function directionalChange(from: number, to: number, up = "提高", down = "下降") {
  const changePct = ((to / from) - 1) * 100;
  if (Math.abs(changePct) < 0.5) return "大致持平";
  return `${changePct > 0 ? up : down}约 ${Math.abs(changePct).toFixed(0)}%`;
}

/** Empirical hit-rate of nominal-90% intervals over genuine OOS backtest points. */
function intervalHitRate(points: Array<{ actual: number; low: number; high: number }>) {
  const evaluable = points.filter((point) =>
    Number.isFinite(point.actual) && Number.isFinite(point.low) && Number.isFinite(point.high));
  const covered = evaluable.filter((point) => point.actual >= point.low && point.actual <= point.high).length;
  return { covered, total: evaluable.length };
}

const FUNDING_TERMS = [
  {
    term: "CapEx / OCF",
    plainLanguage: "每产生 $1 经营现金，有多少被现金资本开支占用。",
    investorMeaning: "低于 1.00× 代表当季经营现金可以覆盖投入；越接近 1.00×，留给回购、并购和其他配置的现金越少。",
  },
  {
    term: "内部融资余量（FCF）",
    plainLanguage: "经营现金流减去现金资本开支后还剩多少钱。",
    investorMeaning: "它不是利润；转负也不等于破产，但意味着扩张更可能动用现金储备、举债或压缩股东回报。",
  },
  {
    term: "8Q 内穿越概率",
    plainLanguage: "加入历史真实预测误差后，未来八个季度中至少一季 CapEx 不低于 OCF 的模拟路径占比。",
    investorMeaning: "这是资金压力事件的概率，不是破产概率，也不是股价下跌概率。",
  },
  {
    term: "历史误差范围",
    plainLanguage: "模型按过去样本外误差估计的未来波动范围；图例中同时标注它在历史上的真实命中率。",
    investorMeaning: "它不是最坏情况，也不是估值区间；命中率低于名义水平时应把它读得更宽。",
  },
];

function arrAnchorKindLabel(kind: string) {
  const labels: Record<string, string> = {
    "official-retrospective": "公司回溯披露",
    "reported-retrospective": "媒体回溯报道",
    "company-reported-run-rate": "公司披露 run-rate",
    "media-reported-run-rate": "媒体报道 run-rate",
    "official-lower-bound": "公司披露下限",
    "official-run-rate": "公司披露 run-rate",
  };
  return labels[kind] ?? "披露锚点";
}

function companyArrDisclosureHistory(result: ResearchModelBundle, company: "openai" | "anthropic") {
  const monthly = new Map<string, { period: string; value: number; detail: string }>();
  const anchors = [...result.labArr.companies[company].anchors]
    .sort((left, right) => left.observedAt.localeCompare(right.observedAt) || left.availableAt.localeCompare(right.availableAt));
  for (const anchor of anchors) {
    const period = anchor.observedAt.slice(0, 7);
    monthly.set(period, {
      period,
      value: anchor.arrB,
      detail: `公开于 ${anchor.availableAt} · ${arrAnchorKindLabel(anchor.kind)}`,
    });
  }
  return [...monthly.values()];
}

function dashboardModels(current: ResearchModelBundle, baseline: ResearchModelBundle): DashboardModel[] {
  const expectations2027 = capexExpectationForYear(2027);
  const expectations2028 = capexExpectationForYear(2028);
  const expectationLeaders2027 = largestBuysideGaps(2027);
  const capex = current.companyCapex;
  const baseCapex = baseline.companyCapex;
  const capexNext = capex.forecast.total.points[0];
  const capexFourth = capex.forecast.total.points[3];
  const capexBacktest = capex.backtest.total.metrics.find((metric) => metric.expert === "bottom-up")
    ?? capex.backtest.total.metrics.at(-1)!;
  const capexHits = intervalHitRate(capex.backtest.total.points);
  const capexIntervalLabel = capexHits.total
    ? `历史误差区间 · 实测命中 ${capexHits.covered}/${capexHits.total}`
    : "历史误差区间";

  const models: DashboardModel[] = [{
    id: "capex",
    label: "公司 CapEx",
    description: MODEL_DESCRIPTIONS.capex,
    plain: MODEL_PLAIN.capex,
    monitor: {
      narrative: {
        question: "五大云厂商现在投入了多少钱建设 AI 基础设施？",
        verdict: `最新共同季度现金资本开支为 ${money(capex.monitor.totalCapex)}`,
        summary: `${capex.monitor.commonPeriod}，Alphabet、Microsoft、Amazon、Meta 与 Oracle 的现金资本开支合计为 ${money(capex.monitor.totalCapex)}。这是五家公司全部现金 CapEx，不等同于纯 AI CapEx。`,
        implication: "资本开支是芯片、数据中心和电力需求最直接的财务前导指标，但投入增长只有在未来转化为收入与现金流时才创造股东价值。",
        watch: `用户提供的 2027 年 Buy-side poll 为 $${expectations2027.buyside2026_07_06.toFixed(1)}B，比同期 Sell-side 高约 ${expectations2027.reportedGapPct}%，显示预期分歧；截图发布方与方法待复核。`,
        statusLabel: "投入高位、市场分歧扩大",
        tone: "watch",
        terms: [
          { term: "现金 CapEx", plainLanguage: "当期实际支付的物业、设备与基础设施现金。", investorMeaning: "比订单或租赁承诺更接近真实现金消耗，但不代表全部 AI 支出。" },
          { term: "共同季度", plainLanguage: "只把五家公司都已披露的同一季度相加。", investorMeaning: "避免把不同季度错位拼在一起制造虚假增长。" },
          { term: "Buy-side / Sell-side 预期差", plainLanguage: "资产管理机构调查值与分析师一致预期的差额。", investorMeaning: "差距越大，财报后的盈利预期修订和股价反应可能越剧烈；当前截图来源仍待复核。" },
        ],
      },
      actual: {
        label: "最新共同季度",
        value: money(capex.monitor.totalCapex, 0),
        detail: capex.monitor.companies.map((company) => company.name).join(" · "),
      },
      asOf: `${capex.monitor.commonPeriod} · ${capex.monitor.latestFilingDate}`,
      health: {
        status: capex.monitor.companies.every((company) => company.ageDays <= 120) ? "current" : "stale",
        label: `${capex.monitor.companies.length} 家公司已对齐`,
        detail: "SEC first-reported vintage",
      },
    },
    forecast: {
      narrative: {
        question: "五大云厂商还会加大 AI 投入吗？",
        verdict: `还在加速：${capexFourth.period} 单季预计 ${money(capexFourth.predicted, 0)}，较最新实际${directionalChange(capex.monitor.totalCapex, capexFourth.predicted, "增长", "回落")}`,
        summary: `相对 ${capex.monitor.commonPeriod} 的 ${money(capex.monitor.totalCapex)}，模型预计四个季度后的单季投入${directionalChange(capex.monitor.totalCapex, capexFourth.predicted)}。外部 Buy-side 对 2027 年口径的合计预期为 $${expectations2027.buyside2026_07_06.toFixed(1)}B。`,
        implication: "CapEx 上修通常先利好芯片、服务器、网络和电力供应链，却可能先压低云厂商自由现金流；真正的估值分水岭是投入回报能否随后兑现。",
        watch: `${expectationLeaders2027.map((row) => `${row.company} +${row.reportedGapPct}%`).join("、")} 的 Buy-side / Sell-side 分歧最大；截图中 2028 年 Big 5 总分歧为 ${expectations2028.reportedGapPct}%，来源待复核。`,
        statusLabel: "投入仍在加速",
        tone: "watch",
        confidenceNote: `模型区间来自 ${capexNext.residualCount} 个历史样本外残差，名义 90% 区间在历史回测中实际命中 ${capexHits.covered}/${capexHits.total}；市场调查截至 ${CAPEX_EXPECTATIONS.asOf}，来自用户截图，发布方与方法待复核。`,
        terms: [
          { term: "基准预测", plainLanguage: "在不额外改变假设时，模型认为最具代表性的未来路径。", investorMeaning: "它不是管理层指引，也不是承诺值。" },
          { term: "预测误差范围", plainLanguage: "根据过去逐季预测曾经错多少，估计未来可能偏离基准的范围。", investorMeaning: "区间越宽，越不适合用单一点位做仓位判断。" },
          { term: "市场预期锚", plainLanguage: "Sell-side 与 Buy-side 对全年 CapEx 的独立调查值。", investorMeaning: "只用来观察预期差，不进入 SEC 历史回测；年度/财年标签不能与季度模型直接混加。" },
        ],
      },
      future: [
        { label: `下季 · ${capexNext.period}`, value: money(capexNext.predicted, 0), detail: `${money(capexNext.low, 0)} — ${money(capexNext.high, 0)}` },
        { label: `一年后 · ${capexFourth.period}`, value: money(capexFourth.predicted, 0) },
        { label: "市场锚 · 2027", value: `$${(expectations2027.buyside2026_07_06 / 1000).toFixed(2)}T`, detail: `Buy-side poll · 比 Sell-side 高 ${expectations2027.reportedGapPct}%` },
      ],
      interval: {
        label: capexHits.total
          ? `名义 90% 历史误差区间 · 回测实测命中 ${capexHits.covered}/${capexHits.total}`
          : "误差参考范围（样本不足，未校准）",
        low: money(capexNext.low),
        high: money(capexNext.high),
      },
      backtest: {
        label: "历史逐季预测的金额典型误差（越低越好）",
        value: money(capexBacktest.rmse),
        observations: capexBacktest.observations,
        comparison: `近期典型金额误差 ${money(capex.forecast.total.selection.recentRmse)} · 简单基线 ${money(capex.forecast.total.selection.lag1RecentRmse)}`,
      },
      scenario: {
        label: `CapEx 强度 ${current.scenario.capexIntensity.toFixed(2)}×`,
        active: current.scenario.capexIntensity !== DEFAULT_RESEARCH_SCENARIO.capexIntensity,
        impact: signed(capexNext.predicted - baseCapex.forecast.total.points[0].predicted, (value) => money(value)),
      },
      evidence: `过去 ${capexHits.total} 次同类预测中 ${capexHits.covered} 次落在阴影范围内`,
      chart: {
        label: "五家公司现金资本开支：历史实际与未来路径",
        unit: "$B",
        history: capex.backtest.total.points.slice(-12).map((point) => ({ period: point.period, value: point.actual })),
        baseline: baseCapex.forecast.total.points.map((point) => ({ period: point.period, value: point.predicted })),
        scenario: capex.forecast.total.points.map((point) => ({ period: point.period, value: point.predicted, low: point.low, high: point.high })),
        intervalLabel: capexIntervalLabel,
        explanation: "实线是已披露现金支出；虚线是模型路径。市场调查是年度口径，只作为独立预期锚，不画进季度曲线。",
      },
    },
  }];

  if (current.tokenDemand && baseline.tokenDemand) {
    const token = current.tokenDemand;
    const baseToken = baseline.tokenDemand;
    const latest = token.monitor.industryDemandProxy.latestValue!;
    const horizon = token.forecast.horizons.month12;
    const tokenPoints = token.backtest.primary.points;
    const tokenHits = {
      covered: tokenPoints.filter((point) => point.intervalCovered).length,
      total: tokenPoints.length,
    };
    const driftSkill = token.backtest.skillVsBaselinesPct.drift;
    models.push({
      id: "token",
      label: "Token 需求",
      description: MODEL_DESCRIPTIONS.token,
      plain: MODEL_PLAIN.token,
      monitor: {
        narrative: {
          question: "AI 使用需求现在是在加速，还是开始降温？",
          verdict: `行业需求代理指数为 ${number(latest)}`,
          summary: `截至 ${token.monitor.industryDemandProxy.latestMonth ?? token.monitor.latestCompleteMonth}，公开开发者活动代理仍处高位。该指数用于观察方向，不等于全球 Token 总量。`,
          implication: "Token 使用量决定推理工作负载，进而影响 GPU 利用率、云收入与单位算力的电力需求；价格下降可能让使用量上升，却不保证收入同比例增长。",
          watch: token.monitor.platformTokenActual.latestValue == null
            ? "OpenRouter 平台实际 Token 尚未成为可用观测，当前结论主要依赖 npm 与 Wikimedia 代理。"
            : `OpenRouter 平台实际已更新至 ${number(token.monitor.platformTokenActual.latestValue, 2)}T Token，可用于检验公开代理是否偏离。`,
          statusLabel: token.monitor.sourceHealth.demandProxy.status === "current" ? "需求代理仍在扩张" : "数据存在缺口",
          tone: token.monitor.sourceHealth.demandProxy.status === "current" ? "positive" : "watch",
          terms: [
            { term: "需求代理指数", plainLanguage: "把开发工具下载量与 AI 页面关注度合成为方向性指标。", investorMeaning: "适合判断加速/减速，不可读作真实 Token 数量或收入。" },
            { term: "平台实际 Token", plainLanguage: "OpenRouter 平台真实路由的 Token 用量。", investorMeaning: "它是单个平台样本，不代表整个行业，但能帮助校验代理方向。" },
          ],
        },
        actual: {
          label: "需求热度指数",
          value: number(latest, 0),
          detail: token.monitor.platformTokenActual.latestValue == null
            ? "OpenRouter 平台实际量待接入"
            : `OpenRouter 平台实际 ${number(token.monitor.platformTokenActual.latestValue, 2)}T`,
        },
        asOf: token.monitor.industryDemandProxy.latestMonth ?? token.monitor.latestCompleteMonth,
        health: {
          status: token.monitor.sourceHealth.demandProxy.status === "current" ? "current" : "degraded",
          label: token.monitor.sourceHealth.demandProxy.status === "current" ? "已更新" : "存在数据缺口",
          detail: `${token.monitor.sourceHealth.demandProxy.coverage.observedMonths} 个完整月`,
        },
      },
      forecast: {
        narrative: {
          question: "AI 使用需求还在加速吗？",
          verdict: `仍在加速：12 个月后需求指数预计 ${number(horizon.predicted, 0)}，较当前${directionalChange(latest, horizon.predicted, "上升", "下降")}`,
          summary: `相对当前 ${number(latest)}，模型路径${directionalChange(latest, horizon.predicted, "上升", "下降")}；历史误差范围为 ${number(horizon.low)}—${number(horizon.high)}（过去 ${tokenHits.total} 次逐月预测中 ${tokenHits.covered} 次命中）。这是需求代理预测，不是 Token 收入预测。`,
          implication: "持续增长支持推理算力与电力需求，但收入还取决于单位价格、缓存、模型效率和工作负载结构。",
          watch: "把需求增长与单位推理价格一起看：量增但价格下降过快时，Token 使用可能上升而收入或毛利并不同步。",
          statusLabel: "增长强、变现仍待验证",
          tone: "watch",
          confidenceNote: `${token.backtest.primary.metrics.full.observations} 个样本外月度观测；相对 lag-1 的历史误差改善为 ${token.backtest.skillVsBaselinesPct.lag1?.toFixed(1) ?? "不可比较"}%${driftSkill != null && driftSkill < 0 ? `；但简单趋势外推的历史误差还要低 ${Math.abs(driftSkill).toFixed(1)}%，主模型优势尚未确立` : ""}。`,
          terms: [
            { term: "12M 基准", plainLanguage: "在当前模型与默认假设下，一年后的需求代理中位路径。", investorMeaning: "它表达方向与量级，不是公司指引。" },
            { term: "价格弹性", plainLanguage: "单位推理价格变化 1%，使用需求会响应多少。", investorMeaning: "用于压力测试降价能否带来足够增量，不代表已识别出的稳定因果关系。" },
            { term: "MAPE", plainLanguage: "模型在历史逐月预测中平均错了实际值的多少百分比。", investorMeaning: "越低越好；不是未来一定保持的准确率。" },
          ],
        },
        future: [
          { label: "12 个月后", value: number(horizon.predicted, 0), detail: `${number(horizon.low, 0)} — ${number(horizon.high, 0)}` },
          { label: "3 个月后", value: number(token.forecast.horizons.month3.predicted, 0) },
          { label: "下月", value: number(token.forecast.horizons.month1.predicted, 0) },
        ],
        interval: {
          label: tokenHits.total
            ? `名义 90% 历史误差区间 · 回测实测命中 ${tokenHits.covered}/${tokenHits.total}`
            : "误差参考范围（样本不足，未校准）",
          low: number(horizon.low),
          high: number(horizon.high),
        },
        backtest: {
          label: "历史逐月预测的平均百分比误差（越低越好）",
          value: `${token.backtest.primary.metrics.full.mape.toFixed(1)}%`,
          observations: token.backtest.primary.metrics.full.observations,
          comparison: token.backtest.skillVsBaselinesPct.lag1 == null
            ? "尚不能与沿用上月数值的简单方法比较"
            : `相对沿用上月数值，历史误差改善 ${token.backtest.skillVsBaselinesPct.lag1.toFixed(1)}%`,
        },
        scenario: {
          label: `需求 ${current.scenario.tokenAnnualDemandGrowthPct.toFixed(0)}% · 价格 ${current.scenario.tokenUnitPriceChangePct.toFixed(0)}%`,
          active: token.forecast.scenarioImpact.affectsCurrentChart,
          impact: signed(horizon.predicted - baseToken.forecast.horizons.month12.predicted, (value) => number(value)),
        },
        evidence: `过去 ${tokenHits.total} 次逐月预测中 ${tokenHits.covered} 次落在阴影范围内`,
        chart: {
          label: "AI 使用需求代理：历史实际与 12M 预测",
          unit: "",
          history: token.monitor.industryDemandProxy.points.slice(-18).map((point) => ({ period: point.month, value: point.value })),
          baseline: baseToken.forecast.points.map((point) => ({ period: point.month, value: point.predicted })),
          scenario: token.forecast.points.map((point) => ({ period: point.month, value: point.predicted, low: point.low, high: point.high })),
          intervalLabel: tokenHits.total ? `历史误差区间 · 实测命中 ${tokenHits.covered}/${tokenHits.total}` : "历史误差区间",
          explanation: "未来曲线同时受需求增长、单位价格变化和价格弹性影响；这些是假设压力测试，不是管理层指引。",
        },
      },
    });
  }

  {
    const token = current.tokenVolume;
    const baseToken = baseline.tokenVolume;
    const currentEstimate = token.estimate.latest;
    const horizon = token.forecast.structural.at(-1)!;
    const baseHorizon = baseToken.forecast.structural.at(-1)!;
    const metrics = token.backtest.primary.metrics.full;
    const scenarioActive = current.scenario.tokenAiPenetrationMultiplier !== DEFAULT_RESEARCH_SCENARIO.tokenAiPenetrationMultiplier
      || current.scenario.tokenAgentSharePct !== DEFAULT_RESEARCH_SCENARIO.tokenAgentSharePct
      || current.scenario.tokenTaskLengthMultiplier !== DEFAULT_RESEARCH_SCENARIO.tokenTaskLengthMultiplier
      || current.scenario.tokenComputeCapacityMultiplier !== DEFAULT_RESEARCH_SCENARIO.tokenComputeCapacityMultiplier
      || current.scenario.tokenAnnualDemandGrowthPct !== DEFAULT_RESEARCH_SCENARIO.tokenAnnualDemandGrowthPct;
    models.push({
      id: "tokenVolume",
      label: "Token 数量",
      description: MODEL_DESCRIPTIONS.tokenVolume,
      plain: MODEL_PLAIN.tokenVolume,
      monitor: {
        narrative: {
          question: "公开可观测的 Token 工作量正在怎样变化？",
          verdict: `OpenRouter 最新完整月为 ${number(token.monitor.openRouter.latestTokensT)}T Token`,
          summary: `截至 ${token.monitor.latestCompleteMonth}，单平台实测环比 ${token.monitor.openRouter.monthOverMonthPct == null ? "暂无可比" : `${token.monitor.openRouter.monthOverMonthPct >= 0 ? "+" : ""}${token.monitor.openRouter.monthOverMonthPct.toFixed(1)}%`}；全球结构基准估计为 ${number(currentEstimate.base)}T/月，但它不是实测市场总量。`,
          implication: "Token 是推理算力与电力需求最直接的工作量指标；Agent 任务更长、循环更多，所以 Token 增速可能显著快于用户数。",
          watch: "平台 Token、全球结构估计和可计费 Token 是三种不同口径。最重要的是趋势是否跨平台一致，以及 Agent 占比与任务长度是否继续上升。",
          statusLabel: "平台趋势加速、绝对量未校准",
          tone: "watch",
          confidenceNote: `${metrics.observations} 个真实样本外月；平台趋势 MAPE ${metrics.mape.toFixed(1)}%。全球 ${number(currentEstimate.low)}—${number(currentEstimate.high)}T 是假设敏感性，不是 90% 置信区间。`,
          terms: [
            { term: "Workload Tokens", plainLanguage: "完成任务过程中处理的全部 Token。", investorMeaning: "决定算力负荷，但不一定全部计费。" },
            { term: "Billable Tokens", plainLanguage: "扣除免费、缓存与订阅内含用量后可变现的 Token。", investorMeaning: "更接近 API 收入驱动，但仍需净价和产品 mix。" },
            { term: "Agent 占比", plainLanguage: "由多步工具调用和循环任务产生的 Token 比例。", investorMeaning: "Agent 占比提高会让 Token 增速高于席位和用户增速。" },
          ],
        },
        actual: {
          label: "OpenRouter 平台实测",
          value: `${number(token.monitor.openRouter.latestTokensT)}T`,
          detail: `全球结构基准 ${number(currentEstimate.base)}T/月 · 未校准`,
        },
        asOf: token.monitor.latestCompleteMonth,
        health: {
          status: "degraded",
          label: "平台实测 + 结构估计",
          detail: `${metrics.observations} 个 OOS 月`,
        },
      },
      forecast: {
        narrative: {
          question: "全球 Token 工作量可能增长到什么量级？",
          verdict: `若近期平台趋势延续，12 个月结构基准为 ${number(horizon.base)}T/月`,
          summary: `当前结构基准 ${number(currentEstimate.base)}T/月，未来低/中/高为 ${number(horizon.low)} / ${number(horizon.base)} / ${number(horizon.high)}T。该范围由任务与渗透率假设形成，不是统计置信区间。`,
          implication: "如果 Token 增速持续显著高于 ARR，单位价格、免费/捆绑用量或推理成本可能在侵蚀变现效率；如果 ARR 同步跟上，资本开支的收入支撑更强。",
          watch: "基准路径继承了 OpenRouter 近期高增长，远期对增速非常敏感；投资判断应优先看未来三个月实际值是否继续落在路径附近。",
          statusLabel: "高增长、宽假设区间",
          tone: "watch",
          confidenceNote: token.backtest.promotionGate.reason,
        },
        future: [
          { label: "12 个月结构基准", value: `${number(horizon.base)}T`, detail: `${number(horizon.low)} — ${number(horizon.high)}T` },
          { label: "当前可计费估计", value: `${number(currentEstimate.billableTokensT)}T` },
          { label: "当前 Agent 占比", value: `${token.estimate.surfaceMix.find((row) => row.surface === "agent")!.sharePct.toFixed(0)}%` },
        ],
        interval: { label: "结构假设敏感性（未校准）", low: `${number(horizon.low)}T`, high: `${number(horizon.high)}T` },
        backtest: {
          label: "OpenRouter 平台趋势样本外 MAPE",
          value: `${metrics.mape.toFixed(1)}%`,
          observations: metrics.observations,
          comparison: `相对 lag-1 RMSE 改善 ${token.backtest.skillVsLag1Pct?.toFixed(1) ?? "不可比较"}% · 全球结构绝对量尚无足够锚点回测`,
        },
        scenario: {
          label: `渗透 ${current.scenario.tokenAiPenetrationMultiplier.toFixed(2)}× · Agent ${current.scenario.tokenAgentSharePct.toFixed(0)}% · 任务 ${current.scenario.tokenTaskLengthMultiplier.toFixed(2)}×`,
          active: scenarioActive,
          impact: signed(horizon.base - baseHorizon.base, (value) => `${number(value)}T`),
        },
        evidence: `${metrics.observations} 个平台 OOS 月 · 全球绝对量未校准`,
        chart: {
          label: "全球月度 Token 结构估计与 12M 假设路径",
          unit: "T",
          historyLabel: "当前结构估计",
          history: [{ period: token.monitor.latestCompleteMonth, value: currentEstimate.base, low: currentEstimate.low, high: currentEstimate.high }],
          baseline: baseToken.forecast.structural.map((point) => ({ period: point.month, value: point.base, low: point.low, high: point.high })),
          scenario: token.forecast.structural.map((point) => ({ period: point.month, value: point.base, low: point.low, high: point.high })),
          intervalLabel: "低/中/高结构假设（未校准）",
          explanation: "当前点和未来带都是结构模型，不是全球实测。历史准确率只适用于旁边披露的 OpenRouter 平台趋势回测。",
        },
      },
    });
  }

  {
    const arr = current.labArr;
    const baseArr = baseline.labArr;
    const openai = arr.companies.openai;
    const anthropic = arr.companies.anthropic;
    const forecastEndMonth = openai.forecast.points.at(-1)!.month;
    const calibrationApplied = Boolean(openai.monitor.currentCalibration && anthropic.monitor.currentCalibration);
    const horizon = openai.forecast.points.at(-1)!.totalArrB + anthropic.forecast.points.at(-1)!.totalArrB;
    const baseHorizon = baseArr.companies.openai.forecast.points.at(-1)!.totalArrB + baseArr.companies.anthropic.forecast.points.at(-1)!.totalArrB;
    const combinedBenchmark = arr.monitor.externalBenchmarks.find((benchmark) => benchmark.scope === "combined");
    const biasLabel = (meanErrorB: number) => Math.abs(meanErrorB) < 0.05
      ? "平均无明显偏差"
      : meanErrorB < 0
        ? `平均低估 ${money(Math.abs(meanErrorB), 1)}`
        : `平均高估 ${money(meanErrorB, 1)}`;
    const scenarioActive = current.scenario.arrCodingPaidUserMultiplier !== DEFAULT_RESEARCH_SCENARIO.arrCodingPaidUserMultiplier
      || current.scenario.arrB2bApiAnnualGrowthPct !== DEFAULT_RESEARCH_SCENARIO.arrB2bApiAnnualGrowthPct
      || current.scenario.arrEnterpriseSeatMultiplier !== DEFAULT_RESEARCH_SCENARIO.arrEnterpriseSeatMultiplier
      || current.scenario.arrEffectivePriceMultiplier !== DEFAULT_RESEARCH_SCENARIO.arrEffectivePriceMultiplier
      || current.scenario.arrComputeCapacityMultiplier !== DEFAULT_RESEARCH_SCENARIO.arrComputeCapacityMultiplier;
    const companyCharts = ([
      { company: "openai" as const, label: "OpenAI", current: openai, base: baseArr.companies.openai },
      { company: "anthropic" as const, label: "Anthropic（Claude）", current: anthropic, base: baseArr.companies.anthropic },
    ]).map(({ company, label, current: companyResult, base: baseCompany }) => {
      const externalMarkers = arr.monitor.externalBenchmarks
        .filter((benchmark) => benchmark.scope === company && !benchmark.calibrationEligible)
        .map((benchmark) => ({
          period: benchmark.asOf.slice(0, 7),
          value: benchmark.valueB,
          kind: "external-benchmark" as const,
          label: `${benchmark.provider} ${benchmark.exactness === "lower-bound" ? "下限参照" : "外部模型参照"}`,
          detail: `${benchmark.asOf} · ${benchmark.exactness === "lower-bound" ? "至少" : "约"} ${money(benchmark.valueB, 0)} · 不进入拟合/回测`,
        }));
      return {
        label: `${label} · ARR / run-rate（当前与预测）`,
        unit: "$B",
        axisTitle: "ARR 年化收入运行率（$B）",
        historyLabel: "公开 ARR 锚点",
        bridgeLabel: "公开锚点 → 模型补齐期",
        baselineLabel: "未来 ARR 预测",
        history: companyArrDisclosureHistory(current, company),
        forecastOrigin: {
          period: baseCompany.monitor.latestCompleteMonth,
          value: baseCompany.monitor.latestCompleteMonthNowcastArrB,
          label: "完整月纯模型 ARR",
          detail: `${baseCompany.monitor.latestCompleteMonth} 月末 ARR 年化速度 · 不等于公司披露`,
        },
        markers: [{
          period: companyResult.monitor.currentDate.slice(0, 7),
          value: companyResult.monitor.currentDateNowcastArrB,
          kind: "current-nowcast" as const,
          label: companyResult.monitor.currentCalibration ? "当前 ARR（外部研究校准）" : "当前 ARR（纯模型）",
          detail: companyResult.monitor.currentCalibration
            ? `${companyResult.monitor.currentDate} · 外部校准 ${money(companyResult.monitor.currentCalibration.valueB, 0)}；纯模型 ${money(companyResult.monitor.statisticalCurrentDateNowcastArrB, 1)} · 非公开披露`
            : `${companyResult.monitor.currentDate} · ${companyResult.forecast.selectedModel} · 非 GAAP 收入`,
        }, ...externalMarkers],
        baseline: baseCompany.forecast.points.map((point) => ({
          period: point.month,
          value: point.totalArrB,
          detail: `Coding ${money(point.codingB, 1)} · B 端 API ${money(point.b2bApiB, 1)} · 订阅 ${money(point.generalSubscriptionsB, 1)} · ${point.withinBacktestedHorizon ? "锚点回测跨度内" : "超出锚点回测跨度"}`,
        })),
        scenario: companyResult.forecast.points.map((point) => ({
          period: point.month,
          value: point.totalArrB,
          detail: `Coding ${money(point.codingB, 1)} · B 端 API ${money(point.b2bApiB, 1)} · 订阅 ${money(point.generalSubscriptionsB, 1)} · ${point.withinBacktestedHorizon ? "锚点回测跨度内" : "超出锚点回测跨度"}`,
        })),
        explanation: `${label} 的纵轴始终是 ARR（当前收入速度年化），不是累计收入。黑线是公开锚点，灰色桥接线补齐到完整月，蓝点是 ${companyResult.monitor.currentDate} 当前 ARR${companyResult.monitor.currentCalibration ? "（外部研究校准，非公开披露）" : "（纯模型）"}，蓝色虚线是之后的 ARR 预测。最长真实锚点预测跨度为 ${companyResult.forecast.validatedHorizonMonths} 个月。`,
      };
    });
    models.push({
      id: "labArr",
      label: "AI Labs ARR",
      description: MODEL_DESCRIPTIONS.labArr,
      plain: MODEL_PLAIN.labArr,
      monitor: {
        narrative: {
          question: "OpenAI 与 Anthropic 已经把 AI 需求变现到什么程度？",
          verdict: `截至 ${arr.monitor.asOf}，两家公司当前 ARR（年化收入运行率）合计 ${money(arr.monitor.combinedCurrentDateNowcastArrB, 1)}`,
          summary: `${calibrationApplied ? "外部研究校准拆分" : "纯模型 nowcast"}：OpenAI ${money(openai.monitor.currentDateNowcastArrB, 0)}、Anthropic ${money(anthropic.monitor.currentDateNowcastArrB, 0)}；未校准纯模型合计 ${money(arr.monitor.combinedStatisticalCurrentDateNowcastArrB, 1)}。${combinedBenchmark ? `SemiAnalysis ${combinedBenchmark.asOf} 公开摘要仅给出两家约 ${money(combinedBenchmark.valueB, 0)}，与拆分合计存在口径/日期差异。` : ""}`,
          implication: "ARR 是 AI 资本周期能否形成收入闭环的核心读数。只有变现增速能追上算力与 CapEx，基础设施投入才更可能转化为可持续回报。",
          watch: `Anthropic 近期增速快于旧趋势拟合，冠军模型已切换为净新增 ARR 速度；但 ${anthropic.backtest.biasDiagnostics.fullWindow.underforecastRatePct.toFixed(0)}% 的历史 OOS 点仍被低估。两家公司渠道总额/净额口径也不完全可比。`,
          statusLabel: "变现加速、披露仍稀疏",
          tone: "watch",
          confidenceNote: `OpenAI：${openai.backtest.primary.metrics.full.observations} 个 OOS、MAPE ${openai.backtest.primary.metrics.full.mape.toFixed(1)}%、${biasLabel(openai.backtest.biasDiagnostics.fullWindow.meanErrorB)}；Anthropic：${anthropic.backtest.primary.metrics.full.observations} 个 OOS、MAPE ${anthropic.backtest.primary.metrics.full.mape.toFixed(1)}%、${biasLabel(anthropic.backtest.biasDiagnostics.fullWindow.meanErrorB)}。`,
          terms: [
            { term: "Run-rate / ARR", plainLanguage: "把最近一段收入速度年化后的数值。", investorMeaning: "反映当前速度，但不等于过去十二个月 GAAP 收入。" },
            { term: "Reported anchor", plainLanguage: "公司原文或可靠媒体披露的当期年化收入节点。", investorMeaning: "是模型校准点，定义和日期必须单独保存。" },
            { term: "Nowcast", plainLanguage: "锚点之后用最新采用与 Token 信号估出的当前值。", investorMeaning: "可用于跟踪方向，不能冒充公司披露。" },
          ],
        },
        actual: {
          label: `当前 ARR（年化收入运行率）· ${arr.monitor.asOf}`,
          value: money(arr.monitor.combinedCurrentDateNowcastArrB, 1),
          detail: `OpenAI ${money(openai.monitor.currentDateNowcastArrB, 0)} + Anthropic ${money(anthropic.monitor.currentDateNowcastArrB, 0)} · ${calibrationApplied ? "外部校准、非公开披露" : "纯模型 nowcast"}`,
        },
        asOf: arr.monitor.asOf,
        health: { status: "degraded", label: "研究基线", detail: `${arr.backtest.totalOosAnchors} 个 OOS 锚点` },
      },
      forecast: {
        narrative: {
          question: "OpenAI 与 Anthropic 的变现速度还会继续上升吗？",
          verdict: `${forecastEndMonth} 月末，两家公司预测 ARR 终点合计 ${money(horizon, 0)}；这是年化收入速度，不是未来 12 个月累计收入`,
          summary: `这里的“12 个月”指预测终点时的 ARR 年化速度，不是未来 12 个月累计收入。路径从当前外部校准水平出发，沿各自冠军模型增速推进；真实锚点验证跨度仅 OpenAI ${openai.forecast.validatedHorizonMonths} 个月、Anthropic ${anthropic.forecast.validatedHorizonMonths} 个月，终点仍是高不确定性外推。`,
          implication: "ARR 若明显弱于 Token 与算力增长，单位经济性和资本回报压力会上升；若企业/API 分部持续扩大，收入质量与需求可见度更强。",
          watch: "当前远期斜率受少量超高速增长锚点影响；超出已观察锚点跨度的月份必须当作情景外推。每出现一个新披露，系统会用上一 vintage 的预测误差重新评估冠军模型。",
          statusLabel: "高增长、小样本",
          tone: "watch",
          confidenceNote: arr.backtest.warning,
        },
        future: [
          { label: `OpenAI · ${forecastEndMonth} 月末 ARR（外推）`, value: money(openai.forecast.points.at(-1)!.totalArrB, 0) },
          { label: `Anthropic · ${forecastEndMonth} 月末 ARR（外推）`, value: money(anthropic.forecast.points.at(-1)!.totalArrB, 0) },
          { label: `两家公司 · 12 个月后 ARR`, value: money(horizon, 0) },
        ],
        interval: { label: "锚点稀疏，暂不提供伪精确置信区间", low: "未校准", high: "未校准" },
        backtest: {
          label: "逐公司 point-in-time MAPE",
          value: `OAI ${openai.backtest.primary.metrics.full.mape.toFixed(1)}% · Anthropic ${anthropic.backtest.primary.metrics.full.mape.toFixed(1)}%`,
          observations: arr.backtest.totalOosAnchors,
          comparison: `OpenAI ${openai.forecast.selectedModel}（${biasLabel(openai.backtest.biasDiagnostics.fullWindow.meanErrorB)}）· Anthropic ${anthropic.forecast.selectedModel}（${biasLabel(anthropic.backtest.biasDiagnostics.fullWindow.meanErrorB)}）`,
        },
        scenario: {
          label: `Coding ${current.scenario.arrCodingPaidUserMultiplier.toFixed(2)}× · API ${current.scenario.arrB2bApiAnnualGrowthPct.toFixed(0)}% · 净价 ${current.scenario.arrEffectivePriceMultiplier.toFixed(2)}×`,
          active: scenarioActive,
          impact: signed(horizon - baseHorizon, (value) => money(value)),
        },
        evidence: `${arr.backtest.totalOosAnchors} 个 OOS 锚点 · Anthropic 历史低估率 ${anthropic.backtest.biasDiagnostics.fullWindow.underforecastRatePct.toFixed(0)}% · 当前层级${calibrationApplied ? "外部校准" : "纯模型"}为 OAI ${money(openai.monitor.currentDateNowcastArrB, 0)} / Anthropic ${money(anthropic.monitor.currentDateNowcastArrB, 0)} · ${forecastEndMonth} 为外推 ARR 终点`,
        charts: companyCharts,
      },
    });
  }

  if (current.computeDeployment) {
    const compute = current.computeDeployment;
    const monitor = compute.monitor.metrics.find((metric) => metric.id === "delivered_compute_index")!;
    const path = compute.forecast.paths.delivered_compute_index;
    const backtest = compute.backtest.metrics.delivered_compute_index;
    const selected = backtest.candidates.find((candidate) => candidate.id === backtest.selectedModel)!;
    models.push({
      id: "compute",
      label: "算力部署",
      description: MODEL_DESCRIPTIONS.compute,
      plain: MODEL_PLAIN.compute,
      monitor: {
        narrative: {
          question: "芯片支出最终转化成了多少可用 AI 算力？",
          verdict: `交付算力指数为 ${number(monitor.value)}`,
          summary: `${compute.monitor.period} 的指数综合了芯片交付、性能与可测覆盖。当前可测组件覆盖 ${monitor.coveragePct.toFixed(0)}%，因此它是研究级方向指标，不是全球 GPU 数量。`,
          implication: "投入只有在芯片交付、安装并形成可用算力后，才会转化为训练、推理能力以及后续电力负荷。",
          watch: "重点观察交付指数是否继续上升，以及测量覆盖能否改善；覆盖过低时，短期跳动可能来自样本变化而非真实行业拐点。",
          statusLabel: "算力扩张、覆盖有限",
          tone: "watch",
          confidenceNote: `当前 ${monitor.observationCount} 个可测组件、${monitor.missingComponentCount} 个缺失组件。`,
          terms: [
            { term: "交付算力指数", plainLanguage: "把不同代际 AI 芯片的交付量与性能换算成同一方向性尺度。", investorMeaning: "适合观察算力扩张速度，不应解读为 H100 数量。" },
            { term: "可测覆盖", plainLanguage: "输入中有多少组件具备可用于生产判断的观测。", investorMeaning: "覆盖越低，指数越适合作为方向信号而非精确行业总量。" },
          ],
        },
        actual: {
          label: "交付算力指数",
          value: number(monitor.value, 0),
          detail: `芯片支出 ${money(compute.monitor.metrics.find((metric) => metric.id === "chip_spend_usd")!.value, 0)}`,
        },
        asOf: `${compute.monitor.period} · ${compute.monitor.asOf}`,
        health: {
          status: backtest.status === "production-baseline" ? "current" : "degraded",
          label: backtest.status === "production-baseline" ? "OOS 基线" : "研究基线",
          detail: `生产可用记录 ${compute.monitor.quality.productionEligibleRows}/${compute.monitor.quality.sourceRows}`,
        },
      },
      forecast: {
        narrative: {
          question: "砸下去的钱变成了多少可用算力？",
          verdict: `持续扩张：${path[3].period} 交付算力指数预计 ${number(path[3].predicted, 0)}，较当前${directionalChange(monitor.value, path[3].predicted, "提高", "下降")}`,
          summary: `较当前 ${number(monitor.value)} ${directionalChange(monitor.value, path[3].predicted, "提高", "下降")}。模型过去相对“沿用上季数值”的简单基线降低了 ${backtest.skillVsLag1Pct.toFixed(1)}% 的误差。`,
          implication: "算力供给增长是 Token 使用和数据中心用电的上游约束；如果需求增速低于算力增速，利用率与芯片议价可能承压。",
          watch: "该切片尚为 research baseline。真正能提升结论可信度的是更多可测芯片组件与更长的共同样本，而不是增加情景旋钮。",
          statusLabel: "研究级上行路径",
          tone: "watch",
          confidenceNote: `${selected.observations} 个共同样本外季度；当前组件可测覆盖 ${monitor.coveragePct.toFixed(0)}%。`,
          terms: [
            { term: "Research baseline", plainLanguage: "数据和回测足以形成可复现研究信号，但还未达到生产级覆盖。", investorMeaning: "可用于方向判断，不宜用单一点位直接做仓位。" },
            { term: "相对 lag-1 skill", plainLanguage: "与“下一期等于上一期”的简单方法相比，历史误差降低多少。", investorMeaning: "为正才说明复杂方法带来额外信息。" },
          ],
        },
        future: [
          { label: `一年后 · ${path[3].period}`, value: number(path[3].predicted, 0), detail: `${number(path[3].low, 0)} — ${number(path[3].high, 0)}` },
          { label: `下季 · ${path[0].period}`, value: number(path[0].predicted, 0) },
        ],
        interval: {
          label: "测量不确定性 + 历史样本外误差范围（未做历史覆盖统计）",
          low: number(path[0].low),
          high: number(path[0].high),
        },
        backtest: {
          label: "历史逐季预测的指数典型误差（越低越好）",
          value: number(selected.rmse),
          observations: selected.observations,
          comparison: `相对沿用上季度数值，历史误差改善 ${backtest.skillVsLag1Pct.toFixed(1)}% · ${backtest.status === "production-baseline" ? "生产基线" : "研究基线"}`,
        },
        scenario: {
          label: "数据驱动基线",
          active: false,
          impact: "此切片不提供无依据的情景乘数",
        },
        evidence: `${selected.observations} 个样本外季度 · 比“沿用上季”误差低 ${backtest.skillVsLag1Pct.toFixed(0)}%`,
        chart: {
          label: "交付算力：历史实际与未来路径",
          unit: "",
          history: backtest.selectedPoints.slice(-12).map((point) => ({ period: point.period, value: point.actual })),
          baseline: path.map((point) => ({ period: point.period, value: point.predicted, low: point.low, high: point.high })),
          intervalLabel: "测量 + 历史误差范围",
          explanation: "此模型没有可独立识别的情景参数，因此只显示数据驱动路径，不提供不会改变结果的假设控件。",
        },
      },
    });
  }

  const funding = current.fundingPressure;
  const baseFunding = baseline.fundingPressure;
  const fundingPoint = funding.forecast.horizons.quarter8;
  const fundingBacktest = {
    "lag-1": funding.backtest.models.lag1,
    "seasonal-lag-4": funding.backtest.models.seasonalLag4,
    "regime-blend": funding.backtest.models.regimeBlend,
    "seasonal-log-trend": funding.backtest.models.seasonalLogTrend,
  }[funding.forecast.selectedModel];
  const fundingGapCompanies = funding.monitor.companies.filter((company) => company.fundingState === "funding-gap");
  const fundingCompanyCount = funding.monitor.companies.length;
  const fundingCrossing = funding.forecast.crossing;
  const fundingRiskLabel = fundingCrossing.calibrated && fundingCrossing.probabilityWithinHorizon != null
    ? percent(fundingCrossing.probabilityWithinHorizon)
    : "未校准";
  const fundingCrossingLabel = fundingCrossing.status === "censored"
    ? `截至 ${fundingCrossing.censoredAfter} 未穿越`
    : fundingCrossing.status === "already-crossed"
      ? `已于 ${fundingCrossing.pointEstimate} 穿越`
      : `预计 ${fundingCrossing.pointEstimate} 首次穿越`;
  const fundingVerdictPrefix = fundingCrossing.status === "censored"
    ? `基准路径截至 ${fundingCrossing.censoredAfter} 未穿越`
    : fundingCrossing.status === "already-crossed"
      ? `当前已于 ${fundingCrossing.pointEstimate} 穿越`
      : `基准路径预计 ${fundingCrossing.pointEstimate} 首次穿越`;
  const earlyCrossingPoint = fundingCrossing.probabilityByQuarter[Math.min(2, fundingCrossing.probabilityByQuarter.length - 1)];
  const fundingHistoryRecent = fundingBacktest.points.slice(-12);
  const thinnestHistoricalGap = fundingHistoryRecent.reduce((worst, point) => point.actual.freeCashFlow < worst.actual.freeCashFlow ? point : worst);
  const widestHistoricalGap = fundingHistoryRecent.reduce((best, point) => point.actual.freeCashFlow > best.actual.freeCashFlow ? point : best);
  const thinnestForecastGap = funding.forecast.points.reduce((worst, point) => point.freeCashFlow < worst.freeCashFlow ? point : worst);
  models.push({
    id: "funding",
    label: "现金流剪刀差",
    description: MODEL_DESCRIPTIONS.funding,
    plain: MODEL_PLAIN.funding,
    monitor: {
      narrative: {
        question: "五大云厂商的经营现金流，还能覆盖资本开支吗？",
        verdict: `还能覆盖，但季度现金缓冲只剩 ${signedMoney(funding.monitor.aggregate.freeCashFlow)}`,
        summary: `${funding.monitor.commonPeriod}，五家公司 OCF 为 ${money(funding.monitor.aggregate.operatingCashFlow)}，现金 CapEx 为 ${money(funding.monitor.aggregate.cashCapex)}，即每 $1 经营现金有 $${funding.monitor.aggregate.capexToOcf.toFixed(2)} 用于资本开支；${fundingGapCompanies.length}/${fundingCompanyCount} 家已出现公司级缺口。`,
        implication: "一旦现金 CapEx 持续超过 OCF，扩张将更依赖现金储备、债务或权益融资，并可能挤压回购、并购及其他资本配置。穿越 1.00× 不等于破产，也不自动等于看空。",
        watch: `${fundingGapCompanies.map((company) => `${company.name} ${money(company.financingGap)}`).join("、")} 已出现缺口；聚合仍为正，是因为其余公司合计余量抵消了它们。`,
        statusLabel: "安全垫偏薄",
        tone: "watch",
        confidenceNote: `数据截至 ${funding.monitor.asOf} · 共同财务季度 ${funding.monitor.commonPeriod} · SEC first-reported。`,
        terms: FUNDING_TERMS,
      },
      actual: {
        label: "CapEx 占经营现金流",
        value: `${funding.monitor.aggregate.capexToOcf.toFixed(2)}×`,
        detail: `现金缓冲 ${signedMoney(funding.monitor.aggregate.freeCashFlow)} · ${fundingGapCompanies.length}/${fundingCompanyCount} 家已现缺口`,
      },
      asOf: funding.monitor.commonPeriod,
      health: {
        status: funding.monitor.aggregate.fundingState === "self-funded" ? "current" : "degraded",
        label: funding.monitor.aggregate.fundingState === "self-funded" ? "经营现金覆盖" : "存在融资缺口",
        detail: `${fundingCompanyCount} 家 SEC PIT 对齐`,
      },
    },
    forecast: {
      narrative: {
        question: "现金流还能撑住这种投入吗？",
        verdict: `${fundingVerdictPrefix}；历史完整 8Q 残差路径${fundingCrossing.calibrated ? `对应 ${fundingRiskLabel} 的越线比例` : `仅 ${fundingCrossing.residualBlocks} 条，概率暂未校准`}`,
        summary: fundingCrossing.calibrated
          ? `${fundingCrossingLabel}；把历史真实样本外预测误差加入未来路径后，${fundingRiskLabel} 的模拟路径在未来八个季度中至少一季达到 CapEx ≥ OCF。基准点路径与风险概率回答的是两个不同问题。`
          : `${fundingCrossingLabel}；当前只有 ${fundingCrossing.residualBlocks} 条完整 8Q 样本外残差路径，不足以把模拟频率当作校准概率。先读基准点路径，待累计至少 9 条完整路径后再显示概率。`,
        implication: "这是一项现金流预警，而不是破产或股价下跌概率。风险升高意味着投资者应提高对自由现金流、债务融资、回购节奏和资本回报兑现速度的要求。",
        watch: `${fundingCrossing.calibrated && earlyCrossingPoint?.cumulativeProbability != null ? `截至 ${earlyCrossingPoint.period} 的累计穿越概率为 ${percent(earlyCrossingPoint.cumulativeProbability)}；` : `点路径${fundingCrossing.status === "projected" ? `在 ${fundingCrossing.pointEstimate} 首次穿越；` : `截至 ${fundingCrossing.censoredAfter} 未穿越；`}`}同时，用户截图中的 2027 年 Buy-side Big 5 CapEx 比 Sell-side 高 ${expectations2027.reportedGapPct}%，是潜在支出上行风险，来源待复核。`,
        statusLabel: "风险窗口已打开",
        tone: "risk",
        confidenceNote: fundingCrossing.calibrated
          ? `风险概率由 ${fundingCrossing.residualBlocks} 条完整历史 8Q 样本外残差路径重抽而来（模拟 ${fundingCrossing.simulationPaths.toLocaleString("zh-CN")} 次只是为了可复现，不增加信息量）；请把 ${fundingRiskLabel} 读作方向性风险信号，而非市场隐含概率。`
          : `当前只有 ${fundingCrossing.residualBlocks} 条完整历史 8Q 样本外残差路径，低于 9 条校准门槛；界面不显示未经验证的穿越概率。`,
        terms: FUNDING_TERMS,
      },
      future: [
        { label: "8Q 内越线比例", value: fundingRiskLabel, detail: fundingCrossing.calibrated ? "至少一季 CapEx ≥ OCF" : `${fundingCrossing.residualBlocks}/9 条完整残差路径` },
        { label: "当前现金缓冲", value: signedMoney(funding.monitor.aggregate.freeCashFlow), detail: `${fundingGapCompanies.length}/${fundingCompanyCount} 家已现公司级缺口` },
        { label: "8Q 内最窄剪刀口", value: signedMoney(thinnestForecastGap.freeCashFlow), detail: `${thinnestForecastGap.period} · 剪刀口 = OCF − 现金 CapEx；近 12 季实际最窄 ${signedMoney(thinnestHistoricalGap.actual.freeCashFlow)}（${thinnestHistoricalGap.period}）、最阔 ${signedMoney(widestHistoricalGap.actual.freeCashFlow)}（${widestHistoricalGap.period}）` },
        { label: "基准点路径", value: fundingCrossing.status === "censored" ? "预测期内未穿越" : fundingCrossingLabel, detail: fundingCrossing.status === "censored" ? `截至 ${fundingCrossing.censoredAfter}` : "首次达到 1.00×" },
      ],
      interval: {
        label: `按 ${fundingPoint.ratioInterval.residualCount} 个相互重叠的历史误差窗口校准${fundingPoint.ratioInterval.calibrated ? "（名义 90%）" : "（样本不足，未校准）"}`,
        low: `${fundingPoint.ratioInterval.low.toFixed(2)}×`,
        high: `${fundingPoint.ratioInterval.high.toFixed(2)}×`,
      },
      backtest: {
        label: "历史逐季预测的比率典型误差（越低越好）",
        value: `约 ${fundingBacktest.metrics.full.ratioRmse.toFixed(2)}×`,
        observations: fundingBacktest.metrics.full.observations,
        comparison: `近期八季度比率误差约 ${fundingBacktest.metrics.recent8.ratioRmse.toFixed(2)}× · 现金余量金额误差约 ${money(fundingBacktest.metrics.full.fcfRmse)}`,
      },
      scenario: {
        label: `CapEx 增速较基准 ${current.scenario.fundingAnnualCapexGrowthDeltaPct >= 0 ? "+" : ""}${current.scenario.fundingAnnualCapexGrowthDeltaPct.toFixed(0)}pp · OCF ${current.scenario.fundingAnnualOcfGrowthDeltaPct >= 0 ? "+" : ""}${current.scenario.fundingAnnualOcfGrowthDeltaPct.toFixed(0)}pp`,
        active: funding.forecast.scenarioImpact.affectsCurrentChart,
        impact: signed(fundingPoint.capexToOcf - baseFunding.forecast.horizons.quarter8.capexToOcf, (value) => `${value.toFixed(2)}×`),
      },
      evidence: fundingCrossing.calibrated
        ? `风险概率基于 ${fundingCrossing.residualBlocks} 条完整历史 8Q 样本外残差路径 · 方向性信号`
        : `仅 ${fundingCrossing.residualBlocks} 条完整历史 8Q 样本外残差路径 · 概率未校准`,
      chart: {
        label: "现金流剪刀差：历史实际与 8Q 路径",
        unit: "×",
        history: fundingBacktest.points.slice(-12).map((point) => ({
          period: point.period,
          value: point.actual.capexToOcf,
          detail: `CapEx ${money(point.actual.cashCapex)} · OCF ${money(point.actual.operatingCashFlow)} · 余量 ${signedMoney(point.actual.freeCashFlow)}`,
        })),
        baseline: baseFunding.forecast.points.map((point) => ({
          period: point.period,
          value: point.capexToOcf,
          detail: `基准 CapEx ${money(point.cashCapex)} · OCF ${money(point.operatingCashFlow)} · 余量 ${signedMoney(point.freeCashFlow)}`,
        })),
        scenario: funding.forecast.points.map((point) => ({
          period: point.period,
          value: point.capexToOcf,
          low: point.ratioInterval.low,
          high: point.ratioInterval.high,
          detail: `当前假设 CapEx ${money(point.cashCapex)} · OCF ${money(point.operatingCashFlow)} · 余量 ${signedMoney(point.freeCashFlow)}`,
        })),
        threshold: { value: 1, label: "1.00× 花光经营现金" },
        intervalLabel: `历史误差区间（${fundingPoint.ratioInterval.residualCount} 个重叠窗口）`,
        explanation: `点预测采用 ${funding.forecast.selectedModel === "regime-blend" ? "AI 投资周期趋势组合" : funding.forecast.selectedModel}，并通过全窗、近期与 direct 1–8Q 样本外门槛。${fundingCrossing.calibrated ? `${fundingRiskLabel} 的越线比例来自重演完整历史残差路径，需与点路径一起读。` : `完整 8Q 残差路径仅 ${fundingCrossing.residualBlocks} 条，因此暂不把模拟频率展示为概率。`}`,
      },
    },
  });

  if (current.powerDemand && baseline.powerDemand) {
    const power = current.powerDemand;
    const basePower = baseline.powerDemand;
    const horizon = power.forecast.horizons.quarter8;
    const powerBacktest = power.backtest.models[power.backtest.selectedBenchmark];
    const powerCoverage = powerBacktest.full.intervalCoverage90Pct;
    models.push({
      id: "power",
      label: "电力需求",
      description: MODEL_DESCRIPTIONS.power,
      plain: MODEL_PLAIN.power,
      monitor: {
        narrative: {
          question: "当前 AI 基础设施大约需要多少数据中心电力？",
          verdict: `设施负载估算为 ${power.monitor.facilityLoadGw.value.toFixed(2)} GW`,
          summary: `${power.monitor.period} 对应季度耗电约 ${power.monitor.quarterlyTwh.value.toFixed(1)} TWh。该数字由芯片存量、利用率和 PUE 推导，是物理估算，不是全球电表实测。`,
          implication: "电力是算力扩张最难快速增加的约束之一。负载上升会影响公用事业资本开支、电网设备、发电资产与数据中心选址。",
          watch: `Epoch 仅覆盖 ${power.monitor.epochCoverage.coveredSites} 个大型站点，用作校准而非全球总量；需要持续观察站点覆盖与项目投产节奏。`,
          statusLabel: "负载上升、仍为研究估算",
          tone: "watch",
          terms: [
            { term: "Facility Load", plainLanguage: "IT 设备与冷却、供配电等设施合计的持续功率需求。", investorMeaning: "更接近数据中心对电网的真实压力。" },
            { term: "PUE", plainLanguage: "数据中心总用电除以 IT 设备用电。", investorMeaning: "越接近 1 越高效；PUE 上升会放大同等算力对应的总电力需求。" },
            { term: "GW / TWh", plainLanguage: "GW 是某一时点的功率，TWh 是一段时间内消耗的电量。", investorMeaning: "一个决定需要多大供电能力，另一个决定发了多少电。" },
          ],
        },
        actual: {
          label: "设施负载估算",
          value: `${power.monitor.facilityLoadGw.value.toFixed(1)} GW`,
          detail: `季度耗电 ${power.monitor.quarterlyTwh.value.toFixed(1)} TWh`,
        },
        asOf: `${power.monitor.period} · Epoch ${power.monitor.epochCoverage.commonWatermark}`,
        health: {
          status: "degraded",
          label: "研究基线",
          detail: `Epoch 覆盖 ${power.monitor.epochCoverage.coveredSites} 个大型站点`,
        },
      },
      forecast: {
        narrative: {
          question: "AI 算力要吃掉多少电？",
          verdict: `${horizon.period} 设施负载预计 ${horizon.facilityLoadGw.value.toFixed(1)} GW，较当前${directionalChange(power.monitor.facilityLoadGw.value, horizon.facilityLoadGw.value, "增加", "减少")}`,
          summary: `较当前 ${power.monitor.facilityLoadGw.value.toFixed(1)} GW ${directionalChange(power.monitor.facilityLoadGw.value, horizon.facilityLoadGw.value, "增加", "减少")}，对应年化用电约 ${horizon.annualizedTwh.value.toFixed(0)} TWh；模型范围为 ${horizon.facilityLoadGw.low.toFixed(1)}—${horizon.facilityLoadGw.high.toFixed(1)} GW。`,
          implication: "若投产路径兑现，电网接入、变压器、燃气发电、核电与储能可能成为资本周期的下一段瓶颈；但项目延期会把需求向后推移。",
          watch: "把部署滞后、利用率和 PUE 分开看：部署滞后改变时间，利用率改变 IT 负载，PUE 只放大设施总负载。",
          statusLabel: "两年负载或翻倍以上",
          tone: "watch",
          confidenceNote: `${powerBacktest.full.observations} 个样本外季度${powerCoverage != null ? `，区间历史命中率 ${powerCoverage.toFixed(0)}%（${powerBacktest.full.intervalObservations} 次评估）` : ""}；Epoch 为当前快照而非逐期保存的 PIT vintage，因此保持 research baseline。`,
          terms: [
            { term: "部署滞后", plainLanguage: "芯片交付到安装并形成可用 IT 功率所需的季度数。", investorMeaning: "主要移动需求发生的时间，不直接改变长期芯片总量。" },
            { term: "利用率", plainLanguage: "已安装 IT 容量中平均真正运行的比例。", investorMeaning: "利用率越高，算力收入机会与电力消耗越高。" },
            { term: "Monte Carlo 范围", plainLanguage: "同时抽取物理输入不确定性后形成的结果分布。", investorMeaning: "用于看风险边界，不是最坏情况。" },
          ],
        },
        future: [
          { label: `两年后 · ${horizon.period}`, value: `${horizon.facilityLoadGw.value.toFixed(1)} GW`, detail: `${horizon.facilityLoadGw.low.toFixed(1)} — ${horizon.facilityLoadGw.high.toFixed(1)} GW` },
          { label: "一年后", value: `${power.forecast.horizons.quarter4.facilityLoadGw.value.toFixed(1)} GW` },
          { label: "下季", value: `${power.forecast.horizons.quarter1.facilityLoadGw.value.toFixed(1)} GW` },
        ],
        interval: {
          label: powerCoverage != null
            ? `物理输入不确定性模拟范围 · 历史命中率 ${powerCoverage.toFixed(0)}%（${powerBacktest.full.intervalObservations} 次评估）`
            : "物理输入不确定性模拟范围",
          low: `${horizon.facilityLoadGw.low.toFixed(2)} GW`,
          high: `${horizon.facilityLoadGw.high.toFixed(2)} GW`,
        },
        backtest: {
          label: "历史逐季预测的站点功率典型误差（越低越好）",
          value: `${powerBacktest.full.rmse.toFixed(3)} GW`,
          observations: powerBacktest.full.observations,
          comparison: `近期八季度典型误差 ${powerBacktest.recent8.rmse.toFixed(2)} GW · 研究基线`,
        },
        scenario: {
          label: `滞后 ${current.scenario.powerDeploymentLagQuarters}Q · 利用率 ${(current.scenario.powerUtilization * 100).toFixed(0)}% · PUE ${current.scenario.powerPue.toFixed(2)}`,
          active: power.forecast.scenarioImpact.active,
          impact: signed(
            horizon.facilityLoadGw.value - basePower.forecast.horizons.quarter8.facilityLoadGw.value,
            (value) => `${value.toFixed(2)} GW`,
          ),
        },
        evidence: powerCoverage != null
          ? `区间历史命中率 ${powerCoverage.toFixed(0)}%（${powerBacktest.full.intervalObservations} 次评估）· 研究估算`
          : "研究估算 · 区间覆盖样本不足",
        chart: {
          label: "数据中心设施负载：当前估算与 8Q 路径",
          unit: " GW",
          history: [{ period: power.monitor.period, value: power.monitor.facilityLoadGw.value }],
          baseline: basePower.forecast.path.map((point) => ({ period: point.period, value: point.facilityLoadGw.value })),
          scenario: power.forecast.path.map((point) => ({ period: point.period, value: point.facilityLoadGw.value, low: point.facilityLoadGw.low, high: point.facilityLoadGw.high })),
          intervalLabel: "物理输入模拟范围",
          explanation: "未来路径由芯片交付、部署滞后、利用率和 PUE 转换而来；拖动假设时只会改变未来，不重写历史回测。",
        },
      },
    });
  }

  return models;
}

function ScenarioEngine({
  modelId,
  scenario,
  model,
  parity,
  onChange,
  onReset,
}: {
  modelId: ModelId;
  scenario: ResearchScenario;
  model: DashboardModel;
  parity: "idle" | "checking" | "verified" | "mismatch";
  onChange: (key: keyof ResearchScenario, value: number) => void;
  onReset: () => void;
}) {
  const controls = SCENARIO_CONTROLS[modelId];
  const hasControls = controls.length > 0;
  const changedCount = controls.filter((control) => scenario[control.key] !== DEFAULT_RESEARCH_SCENARIO[control.key]).length;
  const activeKeys = new Set(controls.map((control) => control.key));
  const otherChangedCount = (Object.keys(DEFAULT_RESEARCH_SCENARIO) as Array<keyof ResearchScenario>)
    .filter((key) => !activeKeys.has(key) && scenario[key] !== DEFAULT_RESEARCH_SCENARIO[key]).length;
  return (
    <aside className="scenario-engine" aria-label="情景引擎">
      <header>
        <div>
          <span className="section-kicker">如果假设改变会怎样</span>
          <h2>假设压力测试</h2>
        </div>
        {parity === "mismatch" ? <span className="parity-state parity-state--mismatch">版本不一致</span> : null}
        {parity === "idle" ? <span className="parity-state parity-state--idle">云端校验不可用</span> : null}
      </header>
      <p>{hasControls ? "这不是另一套预测。它只回答：如果未来假设改变，当前投资结论会怎样变化。" : "该模型没有可独立识别的结构假设，因此只展示数据驱动路径。"}</p>
      {!hasControls ? (
        <div className="scenario-mode-note"><LockSimple size={16} /> 固定数据路径</div>
      ) : null}
      <div className={`scenario-current-impact${hasControls ? "" : " scenario-current-impact--fixed"}`} aria-live="polite">
        <span>{hasControls ? "当前假设对核心结论的影响" : "当前模型路径"}</span>
        <strong>{hasControls ? model.forecast?.scenario.impact ?? "与基准一致" : model.forecast?.scenario.label}</strong>
        <small>{hasControls ? model.forecast?.scenario.label : "源分位 · 共同 OOS 窗口 · 固定 seed"}</small>
      </div>
      {hasControls ? (
        <div className="scenario-controls">
          {controls.map((control) => (
            <label
              key={control.key}
              className={scenario[control.key] !== DEFAULT_RESEARCH_SCENARIO[control.key] ? "is-adjusted" : undefined}
            >
              <span>
                <strong>{control.label}{scenario[control.key] !== DEFAULT_RESEARCH_SCENARIO[control.key] ? <em>已调整</em> : null}</strong>
                <output>{control.format(scenario[control.key])}</output>
              </span>
              <input
                id={`scenario-${String(control.key)}`}
                data-testid={`scenario-${String(control.key)}`}
                type="range"
                min={control.min}
                max={control.max}
                step={control.step}
                value={scenario[control.key]}
                onChange={(event) => onChange(control.key, Number(event.target.value))}
              />
              <span className="scenario-range"><small>{control.format(control.min)}</small><small>{control.format(control.max)}</small></span>
              <small>{control.help}</small>
            </label>
          ))}
        </div>
      ) : (
        <div className="scenario-unavailable">
          <strong>为什么没有滑杆</strong>
          <p>算力部署使用观测源分位、共同样本外窗口和固定 seed 模拟。没有独立可识别的结构参数，因此不展示不会改变当前曲线的控件。</p>
        </div>
      )}
      {hasControls ? (
        <button type="button" className="scenario-reset" onClick={onReset} disabled={!changedCount}>
          <ArrowCounterClockwise size={18} /> 恢复基准 {changedCount ? `· ${changedCount} 项已改` : ""}
        </button>
      ) : null}
      {otherChangedCount ? (
        <div className="scenario-other-changes">其他模型保留 {otherChangedCount} 项已调整假设；它们不影响当前模型。</div>
      ) : null}
      <div className="scenario-lock"><LockSimple size={18} /> 只改未来路径；历史实际与逐期回测保持不变</div>
    </aside>
  );
}

function ModelDetailPanels({ modelId, result }: { modelId: ModelId; result: ResearchModelBundle }) {
  const panels: Array<{ mode: "monitor" | "forecast"; title: string; hint: string }> = [
    { mode: "monitor", title: "当前数据明细", hint: "公司拆分、口径、数据健康与覆盖" },
    { mode: "forecast", title: "预测方法与回测明细", hint: "计算公式、历史回测、区间方法与数据来源" },
  ];
  return (
    <>
      {panels.map((panel) => (
        <details key={panel.mode} className="model-detail">
          <summary>
            <span><strong>{panel.title}</strong><small>{panel.hint}</small></span>
          </summary>
          <div className="model-detail__body">
            {modelId === "capex" ? <CompanyCapexPanel result={result.companyCapex} mode={panel.mode} /> : null}
            {modelId === "token" && result.tokenDemand ? <TokenDemandPanel result={result.tokenDemand} mode={panel.mode} /> : null}
            {modelId === "tokenVolume" ? <TokenVolumePanel result={result.tokenVolume} mode={panel.mode} /> : null}
            {modelId === "labArr" ? <LabArrPanel result={result.labArr} mode={panel.mode} /> : null}
            {modelId === "compute" && result.computeDeployment ? <ComputeDeploymentPanel result={result.computeDeployment} mode={panel.mode} /> : null}
            {modelId === "funding" ? <FundingPressurePanel result={result.fundingPressure} mode={panel.mode} /> : null}
            {modelId === "power" && result.powerDemand ? <PowerDemandPanel result={result.powerDemand} mode={panel.mode} /> : null}
          </div>
        </details>
      ))}
    </>
  );
}

export function centeredSwitcherScrollLeft({
  containerWidth,
  scrollWidth,
  itemLeft,
  itemWidth,
}: {
  containerWidth: number;
  scrollWidth: number;
  itemLeft: number;
  itemWidth: number;
}) {
  const maxScrollLeft = Math.max(0, scrollWidth - containerWidth);
  const centered = itemLeft + itemWidth / 2 - containerWidth / 2;
  return Math.min(maxScrollLeft, Math.max(0, centered));
}

function DetailZone({
  models,
  selectedModelId,
  scenario,
  parity,
  result,
  onSelect,
  onScenarioChange,
  onScenarioReset,
}: {
  models: DashboardModel[];
  selectedModelId: ModelId;
  scenario: ResearchScenario;
  parity: "idle" | "checking" | "verified" | "mismatch";
  result: ResearchModelBundle;
  onSelect: (id: ModelId) => void;
  onScenarioChange: (key: keyof ResearchScenario, value: number) => void;
  onScenarioReset: () => void;
}) {
  const switcherRef = useRef<HTMLElement>(null);
  const selectedIndex = Math.max(0, models.findIndex((item) => item.id === selectedModelId));
  const previousModel = models[(selectedIndex - 1 + models.length) % models.length];
  const nextModel = models[(selectedIndex + 1) % models.length];

  useEffect(() => {
    const switcher = switcherRef.current;
    const selectedButton = switcher?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    if (!switcher || !selectedButton) return;
    switcher.scrollTo({
      left: centeredSwitcherScrollLeft({
        containerWidth: switcher.clientWidth,
        scrollWidth: switcher.scrollWidth,
        itemLeft: selectedButton.offsetLeft,
        itemWidth: selectedButton.offsetWidth,
      }),
      behavior: "smooth",
    });
  }, [selectedModelId]);

  const model = models.find((item) => item.id === selectedModelId);
  if (!model?.forecast?.narrative) {
    return (
      <section id="model-detail-zone" className="detail-zone" role="status">
        <div className="detail-zone__frame">
          <header className="win-bar detail-zone__bar"><h2>该模型输入尚未发布</h2></header>
          <div className="detail-zone__grid">
            <p>{selectedModelId === "token" ? result.availability.tokenDemand : selectedModelId === "compute" ? result.availability.computeDeployment : result.availability.powerDemand}</p>
          </div>
        </div>
      </section>
    );
  }
  const forecast = model.forecast;
  const detailCharts = forecast.charts?.length ? forecast.charts : forecast.chart ? [forecast.chart] : [];
  const narrative = forecast.narrative!;
  const monitorNarrative = model.monitor?.narrative;
  return (
    <section id="model-detail-zone" className="detail-zone" aria-label="所选模型的深入研究与压力测试">
      <div className="detail-zone__frame">
      <header className="win-bar detail-zone__bar">
        <div>
          <span className="section-kicker">投资问题 · 深入研究</span>
          <h2>{narrative.question}</h2>
        </div>
        <div className="detail-zone__nav-cluster">
          <button
            className="detail-zone__step"
            type="button"
            aria-label={`上一项：${previousModel.label}`}
            title={`上一项：${previousModel.label}`}
            onClick={() => onSelect(previousModel.id as ModelId)}
          >
            <CaretLeft size={18} weight="bold" aria-hidden="true" />
          </button>
          <nav ref={switcherRef} className="detail-zone__switcher" aria-label="切换研究模型">
            {models.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={item.id === selectedModelId}
              onClick={() => onSelect(item.id as ModelId)}
            >
              {item.label}
            </button>
            ))}
          </nav>
          <button
            className="detail-zone__step"
            type="button"
            aria-label={`下一项：${nextModel.label}`}
            title={`下一项：${nextModel.label}`}
            onClick={() => onSelect(nextModel.id as ModelId)}
          >
            <CaretRight size={18} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="detail-zone__grid">
        <div className="detail-zone__main">
          <div className={`detail-verdict detail-verdict--${narrative.tone}`}>
            <span>{narrative.statusLabel}</span>
            <h3>{narrative.verdict}</h3>
            <p>{narrative.summary}</p>
          </div>
          {detailCharts.length ? (
            <div className={`detail-chart-well${detailCharts.length > 1 ? " detail-chart-well--split" : ""}`}>
              {detailCharts.map((chart) => (
                <section className="detail-chart" key={chart.label} aria-label={chart.label}>
                  {detailCharts.length > 1 ? <h3>{chart.label}</h3> : null}
                  <MetricChart chart={chart} detailed />
                </section>
              ))}
            </div>
          ) : null}
          {detailCharts.some((chart) => chart.explanation) ? (
            <p className="detail-chart-note">{detailCharts.map((chart) => chart.explanation).filter(Boolean).join(" ")}</p>
          ) : null}
          <dl className="detail-numbers">
            {forecast.future.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
                {item.detail ? <small>{item.detail}</small> : null}
              </div>
            ))}
          </dl>
          <div className="detail-evidence">
            <article>
              <span>未来可能落在哪</span>
              <strong>{forecast.interval.low} — {forecast.interval.high}</strong>
              <small>{forecast.interval.label}</small>
            </article>
            <article>
              <span>模型过去错多少</span>
              <strong>{forecast.backtest.value}</strong>
              <small>{forecast.backtest.label} · {forecast.backtest.observations} 个历史样本外观测</small>
              {forecast.backtest.comparison ? <em>{forecast.backtest.comparison}</em> : null}
            </article>
            <article>
              <span>假设压力测试</span>
              <strong>{forecast.scenario.label}</strong>
              <small>{forecast.scenario.active ? "你调整的假设已作用于未来路径" : "当前没有偏离模型基准"}</small>
              {forecast.scenario.impact ? <em>{forecast.scenario.impact}</em> : null}
            </article>
          </div>
          <div className="detail-notes">
            <div><span>为什么重要</span><p>{narrative.implication}</p></div>
            <div><span>接下来盯什么</span><p>{narrative.watch}</p></div>
          </div>
          {narrative.confidenceNote ? <p className="detail-confidence">{narrative.confidenceNote}</p> : null}
          {monitorNarrative && model.monitor ? (
            <div className="detail-monitor-meta">
              <span>{model.monitor.actual.label} <strong>{model.monitor.actual.value}</strong></span>
              <span>数据截至 {model.monitor.asOf}</span>
              <span className={`health-chip health-chip--${model.monitor.health.status}`}>{model.monitor.health.label}</span>
              {model.monitor.health.detail ? <span>{model.monitor.health.detail}</span> : null}
            </div>
          ) : null}
          <InvestorGlossary terms={narrative.terms} />
          <ModelDetailPanels modelId={selectedModelId} result={result} />
        </div>
        <ScenarioEngine
          modelId={selectedModelId}
          scenario={scenario}
          model={model}
          parity={parity}
          onChange={onScenarioChange}
          onReset={onScenarioReset}
        />
      </div>
      </div>
    </section>
  );
}

const PARITY_MISMATCH_MESSAGE = "浏览器模型与 Worker 输出不一致；已保留本地结果并标记版本校验失败。";
const PARITY_RELATIVE_TOLERANCE = 1e-10;

function parityValues(result: ResearchModelBundle): Array<number | null> {
  return [
    result.companyCapex.forecast.total.points[0].predicted,
    result.tokenDemand?.forecast.horizons.month12.predicted ?? null,
    result.tokenVolume.forecast.structural.at(-1)?.base ?? null,
    Object.values(result.labArr.companies).reduce((sum, company) => sum + (company.forecast.points.at(-1)?.totalArrB ?? 0), 0),
    result.computeDeployment?.forecast.paths.delivered_compute_index[0].predicted ?? null,
    result.fundingPressure.forecast.horizons.quarter8.capexToOcf,
    result.powerDemand?.forecast.horizons.quarter8.facilityLoadGw.value ?? null,
  ];
}

export function forecastParityMatches(left: ResearchModelBundle, right: ResearchModelBundle) {
  const leftValues = parityValues(left);
  const rightValues = parityValues(right);
  return leftValues.every((value, index) => {
    const other = rightValues[index];
    if (value === null || other === null) return value === other;
    if (!Number.isFinite(value) || !Number.isFinite(other)) return false;
    const scale = Math.max(1, Math.abs(value), Math.abs(other));
    return Math.abs(value - other) <= scale * PARITY_RELATIVE_TOLERANCE;
  });
}

export default function App({ initialModelId = "funding" }: AppProps) {
  const fallbackPanel = seed as DataPanel;
  const [panel, setPanel] = useState<RuntimePanel>(fallbackPanel);
  const [selectedModelId, setSelectedModelId] = useState<ModelId>(initialModelId);
  const [scenario, setScenario] = useState<ResearchScenario>({ ...DEFAULT_RESEARCH_SCENARIO });
  const [dataState, setDataState] = useState<"loading" | "live" | "fallback">("loading");
  const [message, setMessage] = useState("");
  const [parity, setParity] = useState<"idle" | "checking" | "verified" | "mismatch">("idle");

  const baseline = useMemo(() => runResearchModels(panel), [panel]);
  const result = useMemo(() => runResearchModels(panel, scenario), [panel, scenario]);
  // The wall always shows the untouched baseline; stress-test overlays live in the detail zone.
  const wallModels = useMemo(() => dashboardModels(baseline, baseline), [baseline]);
  const models = useMemo(() => dashboardModels(result, baseline), [result, baseline]);

  useEffect(() => {
    if (import.meta.env?.DEV) {
      setDataState("fallback");
      return;
    }
    const controller = new AbortController();
    fetch("/api/data", { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)))
      .then((nextPanel: RuntimePanel) => {
        setPanel(nextPanel);
        setDataState("live");
      })
      .catch((error: Error) => {
        if (error.name === "AbortError") return;
        setDataState("fallback");
        setMessage(`实时数据读取失败（${error.message}）；当前显示同版本内置快照。`);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (import.meta.env?.DEV) {
      setParity("idle");
      return;
    }
    const controller = new AbortController();
    setParity("checking");
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams(
        Object.entries(scenario).map(([key, value]) => [key, String(value)]),
      );
      fetch(`/api/forecast?${query}`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)))
        .then((remote: ResearchModelBundle) => {
          const matches = forecastParityMatches(remote, result);
          setParity(matches ? "verified" : "mismatch");
          setMessage((current) => matches
            ? (current === PARITY_MISMATCH_MESSAGE ? "" : current)
            : PARITY_MISMATCH_MESSAGE);
        })
        .catch((error: Error) => {
          if (error.name === "AbortError") return;
          setParity("idle");
        });
    }, 240);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [result, scenario]);

  const setScenarioValue = (key: keyof ResearchScenario, value: number) => {
    setScenario((current) => ({ ...current, [key]: value }));
  };

  const resetCurrentScenario = () => {
    setScenario((current) => {
      const next = { ...current };
      for (const control of SCENARIO_CONTROLS[selectedModelId]) next[control.key] = DEFAULT_RESEARCH_SCENARIO[control.key];
      return next;
    });
  };

  const openDetail = (id: string) => {
    setSelectedModelId(id as ModelId);
    if (typeof document !== "undefined") {
      document.getElementById("model-detail-zone")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <header className="topbar">
        <div className="brand-block">
          <span className="brand-mark">A</span>
          <div><strong>AI 资本周期监控</strong><small>公开数据 · 样本外验证</small></div>
        </div>
        <div className="topbar-state">
          <span className={`data-state data-state--${dataState}`}>
            {dataState === "live" ? <CheckCircle size={18} /> : dataState === "loading" ? <Pulse size={18} /> : <WarningCircle size={18} />}
            {dataState === "live" ? "实时面板" : dataState === "loading" ? "读取数据" : "快照模式"}
          </span>
          <small>{panel.meta.asOf} · {Object.keys(panel.companies).length} 家公司</small>
        </div>
      </header>

      <main id="main-content" className="main-stage">
        {message ? <div className="system-message" role="status"><WarningCircle size={18} /><span>{message}</span></div> : null}

        <RealtimeMonitorBoard result={baseline} />

        <section className="page-head page-head--forecast">
          <span className="section-kicker">FORECAST · 七个可检验问题</span>
          <h1>从使用量到资本回报</h1>
          <p>
            沿着「Token 使用 → ARR 变现 → CapEx → 算力 → 电力 → 现金流」读七个模型。
            <strong>实线是事实或明确标注的当前结构估计，虚线是模型路径；每个区间都说明它是否真的经过历史校准。</strong>
          </p>
        </section>

        <ChartWall models={wallModels} selectedModelId={selectedModelId} onOpen={openDetail} />

        <DetailZone
          models={models}
          selectedModelId={selectedModelId}
          scenario={scenario}
          parity={parity}
          result={result}
          onSelect={(id) => setSelectedModelId(id)}
          onScenarioChange={setScenarioValue}
          onScenarioReset={resetCurrentScenario}
        />

        <div className="footer-groove" aria-hidden="true" />
        <footer className="research-footer">
          <span>AI Capital Cycle</span>
          <p>公开数据、point-in-time 财务口径与样本外回测；用于研究，不构成证券推荐。</p>
        </footer>
      </main>
    </div>
  );
}
